<template>
  <div class="assets-map" :class="{ 'assets-map--dark': isDarkTheme }">
    <div ref="map" class="assets-map__canvas" />

    <p v-if="!markableAssets.length" class="assets-map__empty">
      {{ $t('attention-map-panel.map-empty') }}
    </p>

    <map-legend class="assets-map__legend" />
  </div>
</template>

<script>
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import { AssetTypes } from 'sigma-mappings';
import { alarmCategories } from '@/domain/enums/alarmCategory';
import {
  assetConditions,
  assetConditionsBySeverity,
} from '@/domain/enums/assetCondition';
import {
  countActedAlarms,
  countedClassifications,
  getWorstCondition,
} from '@/domain/services/generalPanel/attentionMapPanel/attentionMapAggregator';
import MapLegend from './MapLegend';

/** Brasil inteiro no primeiro desenho, que é o escopo mais comum. */
const DEFAULT_CENTER = [-14.8, -52.5];
const DEFAULT_ZOOM = 4;
const MIN_ZOOM = 3;
/** Teto do enquadramento inicial: uma instalação só não vira zoom de rua. */
/**
 * Espera antes de anunciar um recorte novo. A roda do mouse dispara um `moveend`
 * por parada, e cada anúncio custa uma chamada à API dos Últimos Alarmes.
 */
const VIEWPORT_DEBOUNCE_IN_MS = 400;

const FIT_MAX_ZOOM = 9;
/**
 * Zoom a partir do qual o leque de Ativos abre sozinho.
 *
 * Abaixo disso um leque aberto atravessaria estados no mapa, e a tela vira um
 * emaranhado de linhas. Daqui para cima o operador já está olhando uma região, e
 * o que ele quer ver são os Ativos, não um círculo que esconde.
 */
const AUTO_SPIDERFY_ZOOM = 10;
const FIT_PADDING = [40, 40];
/**
 * Mesmo basemap do mapa de Monitoramento: os tiles públicos do OpenStreetMap.
 * No tema escuro eles são escurecidos por filtro CSS, e não trocados por um
 * provedor de basemap escuro — provedor é serviço contratado. Ver
 * docs/adr/0011-basemap-do-carto-no-mapa-de-atencao.md
 */
const TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const TILE_ATTRIBUTION = '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> '
  + 'contributors';

/**
 * O agrupamento veste o ícone de instalação de energia, o mesmo que Alarmes e
 * Avisos usa, na variante `currentColor` — veste a cor da Condição sem asset
 * novo. Quem troca instalação por ativo é o próprio agrupamento, que já abre e
 * fecha conforme o zoom. Ver Marcador do Ativo no CONTEXT.md
 */
const INSTALLATION_GLYPH = '/static/imgs/electrical-network-dynamic.svg#electrical-network-svg';

/**
 * O desenho de cada tipo de Ativo, os três em `currentColor` e no mesmo peso: o
 * marcador é lido a três metros, e o que distingue um tipo do outro nesse tamanho
 * é a silhueta.
 */
/**
 * Tamanho do marcador e do agrupamento, em px, na tela de referência.
 *
 * O Leaflet desenha o marcador em pixel fixo, e o ponto de ancoragem é ele que
 * calcula — não dá para crescer o pino só no CSS sem soltá-lo da coordenada. Daí
 * a escala vir daqui: o mesmo tratamento proporcional do resto do painel, feito
 * onde o Leaflet consegue enxergar.
 */
const PIN_BASE = 30;
// O agrupamento é quase o dobro do pino: ele carrega o número, o anel de
// composição e a resposta de "tem coisa grave aqui?". Crescer não custa nada —
// agrupamentos são poucos e se afastam sozinhos conforme o zoom —, enquanto
// encolher o pino custaria a silhueta do tipo do Ativo, que a 30px já é o mínimo
// para distinguir disjuntor de seccionadora.
const CLUSTER_BASE = 56;
/**
 * Fatia mínima de cada Condição presente no anel do agrupamento. Com quatro
 * condições, o piso soma 48% e sobram 52% para a proporção real.
 */
const MIN_RING_SHARE = 0.12;
/**
 * Raio em que mora o número de cada fatia, em fração de `--cluster-size`.
 *
 * É o meio da faixa do anel: ela vai de 0,504 (o furo da máscara) a 0,84 (a
 * borda externa) do tamanho do agrupamento. Os dois números vêm do CSS, em
 * attention-map.sass, e precisam andar junto com ele.
 */
const RING_LABEL_RADIUS = 0.672;
/**
 * Fatia abaixo da qual o número não é desenhado: o arco não segura dígito.
 *
 * Rede de segurança, e não regra ativa — o piso de `MIN_RING_SHARE` já garante
 * 12% a toda Condição presente, acima deste corte. Existe para o dia em que o
 * piso mudar.
 */
const MIN_LABEL_SHARE = 0.1;
/** Largura do mapa em que o marcador tem exatamente o tamanho base. */
const SCALE_REFERENCE = 1280;
/**
 * Teto de escala do balão, menor que o do marcador.
 *
 * O marcador cresce até 2,4× sem atrapalhar: ele é um pino sobre o mapa. O balão
 * ocupa área, e na mesma escala passou a cobrir metade da vista — o mapa sumia
 * atrás da informação sobre um ponto dele.
 */
const POPUP_MAX_SCALE = 1.5;
/** Fração da largura do mapa que o balão pode ocupar, em qualquer escala. */
const POPUP_MAX_WIDTH_RATIO = 0.32;
/** Limites absolutos do balão, em pixel: legível no celular, contido na TV. */
const POPUP_MIN_WIDTH = 228;
const POPUP_MAX_WIDTH = 460;
/** Teto: numa TV 4K um pino sem limite viraria adesivo. */
const MAX_SCALE = 2.4;
/** A gota é mais alta que larga; a ponta fica abaixo do círculo. */
const PIN_HEIGHT_RATIO = 1.27;

const ASSET_GLYPHS = {
  [AssetTypes.TRANSFORMER]: '/static/imgs/transformer-dynamic.svg#transformer-svg',
  [AssetTypes.DISCONNECTOR]: '/static/imgs/disconnector-dynamic.svg#disconnector-svg',
  [AssetTypes.CIRCUIT_BREAKER]: '/static/imgs/circuit-breaker-dynamic.svg#circuit-breaker-svg',
};

/**
 * Raio, para tipo que o front ainda não conheça: diz "ativo de energia" sem
 * mentir qual é. Hoje o cadastro só tem os três acima.
 */
const GENERIC_ASSET_GLYPH = 'fas fa-bolt';

/**
 * Ícone da Condição no popup, o mesmo do Placar: a mesma
 * condição não pode ter dois desenhos em duas partes da mesma tela.
 */
const CONDITION_GLYPHS = {
  [assetConditions.CRITICAL]: 'fas fa-exclamation-circle',
  [assetConditions.ATTENTION]: 'fas fa-exclamation-triangle',
  [assetConditions.NORMAL]: 'fas fa-check-circle',
  [assetConditions.OUT_OF_SERVICE]: 'fas fa-minus-circle',
};


function glyph(className, href) {
  return `<svg class="${className}" viewBox="0 0 11 13" aria-hidden="true">`
    + `<use xlink:href="${href}"></use></svg>`;
}

function escapeHtml(value) {
  return String(value === undefined || value === null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export default {
  name: 'AssetsMap',
  components: {
    MapLegend,
  },
  props: {
    assets: {
      type: Array,
      default: () => [],
    },
    /**
     * Desenho do equipamento dentro do marcador. Desligado, sobram a gota e a
     * cor da Condição — que é o que se lê de longe.
     */
    showIcons: {
      type: Boolean,
      default: true,
    },
    /**
     * Contagem de cada Condição sobre a própria fatia do anel do agrupamento.
     *
     * Desligada, o anel volta a ser só a proporção. O número do centro é o
     * **total** e não depende disto: sem ele o agrupamento não diz quantos
     * esconde, que é a razão de existir.
     */
    showSliceCounts: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    /**
     * Tema do perfil. No escuro a camada de tiles recebe um filtro, senão o mapa
     * claro vira um retângulo aceso no meio da tela e ofusca quem passa pela TV.
     */
    isDarkTheme() {
      return this.$store.getters.theme !== 'light';
    },
    /**
     * Só entra no mapa quem tem coordenada. Instalação sem cadastro de
     * latitude e longitude continua no Placar e na lista: sumir do mapa não pode
     * virar sumir da tela. Ver
     * docs/adr/0010-marcador-do-ativo-sem-coordenada-propria.md
     */
    markableAssets() {
      return this.assets.filter(
        (asset) => Number.isFinite(asset.latitude) && Number.isFinite(asset.longitude),
      );
    },
  },
  watch: {
    markableAssets: 'drawMarkers',
    showIcons: 'drawMarkers',
    showSliceCounts: 'drawMarkers',
  },
  mounted() {
    this.createMap();
    this.drawMarkers();
  },
  beforeDestroy() {
    this.destroyMap();
  },
  methods: {
    createMap() {
      this.map = L.map(this.$refs.map, {
        center: DEFAULT_CENTER,
        zoom: DEFAULT_ZOOM,
        minZoom: MIN_ZOOM,
        zoomControl: true,
      });

      L.tileLayer(TILE_URL, { attribution: TILE_ATTRIBUTION }).addTo(this.map);

      /**
       * Ativos da mesma Instalação caem na mesma coordenada e se abrem em leque
       * ao clique — nenhuma coordenada é inventada. `spiderfyDistanceMultiplier`
       * abre o leque largo o bastante para o rótulo do ativo caber.
       */
      this.clusters = L.markerClusterGroup({
        showCoverageOnHover: false,
        spiderfyDistanceMultiplier: 1.6,
        iconCreateFunction: this.createClusterIcon,
      });

      this.map.addLayer(this.clusters);
      // Redesenha quando o mapa muda de tamanho: a escala do marcador vem da
      // largura dele, e sem isso o pino ficaria no tamanho da primeira carga.
      this.map.on('resize', this.drawMarkers);
      // `animationend` do próprio agrupamento, e não `zoomend` do mapa: durante a
      // animação de zoom o Leaflet.markercluster recusa abrir o leque, em
      // silêncio, e o `zoomend` chega antes de a animação terminar.
      this.clusters.on('animationend', this.openStuckClusters);
      // `moveend` cobre zoom e arrasto, que é o que muda o recorte: quem aproxima
      // numa regional quer a lista daquela regional.
      this.map.on('moveend', this.scheduleViewportChange);
      this.hasFitted = false;
    },
    destroyMap() {
      this.cancelViewportChange();

      if (this.map) {
        this.map.remove();
        this.map = null;
        this.clusters = null;
      }
    },
    /**
     * Adia o aviso de recorte novo.
     *
     * Um arrasto dispara `moveend` uma vez, mas a roda do mouse dispara um por
     * parada intermediária: sem a espera, atravessar três níveis de zoom viraria
     * três chamadas à API das quais só a última interessa.
     */
    scheduleViewportChange() {
      this.cancelViewportChange();
      this.viewportTimer = setTimeout(this.emitViewport, VIEWPORT_DEBOUNCE_IN_MS);
    },
    cancelViewportChange() {
      if (this.viewportTimer) {
        clearTimeout(this.viewportTimer);
        this.viewportTimer = null;
      }
    },
    /**
     * Anuncia quais Instalações estão dentro do recorte visível.
     *
     * **A Instalação sem coordenada entra sempre.** Um filtro de lugar não pode
     * excluir o que não tem lugar: ela nunca teve marcador (ver
     * docs/adr/0010-marcador-do-ativo-sem-coordenada-propria.md), e tirá-la da
     * lista por isso seria sumir com o alarme dela em silêncio, sem que ninguém
     * pudesse sequer procurá-la no mapa.
     */
    emitViewport() {
      this.viewportTimer = null;

      if (!this.map) {
        return;
      }

      const bounds = this.map.getBounds();
      const inside = this.assets.filter((asset) => {
        const placed = Number.isFinite(asset.latitude) && Number.isFinite(asset.longitude);

        return !placed || bounds.contains([asset.latitude, asset.longitude]);
      });

      this.$emit('viewport', [...new Set(inside.map((asset) => asset.installationId))]);
    },
    /**
     * Reparte o anel do agrupamento entre as Condições que ele esconde.
     *
     * Proporcional, com **piso**: toda Condição presente ocupa pelo menos
     * `MIN_RING_SHARE` do anel. Sem o piso, um crítico entre duzentos normais
     * viraria uma fatia de 1,8° e sumiria a três metros — e trocar "tudo parece
     * crítico" por "o crítico desapareceu" é piorar, porque ninguém vai até onde
     * não vê.
     *
     * @param {String[]} conditions Condições dos Ativos escondidos.
     * @returns {Array} Pares de condição e fatia, do mais grave ao menos grave.
     */
    ringShares(conditions) {
      const present = assetConditionsBySeverity
        .map((condition) => ({
          condition,
          count: conditions.filter((item) => item === condition).length,
        }))
        .filter((slice) => slice.count > 0);

      if (present.length < 2) {
        return present.map((slice) => [slice.condition, 1]);
      }

      const total = present.reduce((sum, slice) => sum + slice.count, 0);
      const free = 1 - (MIN_RING_SHARE * present.length);

      return present.map(
        (slice) => [slice.condition, MIN_RING_SHARE + ((slice.count / total) * free)],
      );
    },
    /**
     * O anel do agrupamento, como paradas de um `conic-gradient`.
     *
     * Navegador que não conheça `conic-gradient` ignora a variável e cai no
     * preenchimento sólido da pior Condição, que é o desenho antigo: pior, porém
     * inteiro. Numa TV com navegador embarcado velho isso importa.
     */
    ringGradient(shares) {
      let start = 0;

      const stops = shares.map(([condition, share]) => {
        const end = start + (share * 360);
        const color = `var(--condition-${condition})`;
        const stop = `${color} ${start.toFixed(2)}deg ${end.toFixed(2)}deg`;

        start = end;

        return stop;
      });

      return `conic-gradient(${stops.join(', ')})`;
    },
    /**
     * O número de cada Condição, posicionado sobre a própria fatia do anel.
     *
     * O piso de `MIN_RING_SHARE` distorce a proporção de propósito, para o raro
     * não sumir. O número desfaz a distorção: a fatia diz "existe", o número diz
     * "quantos". Sem ele, o anel de um crítico entre duzentos normais e o de
     * vinte e quatro entre duzentos são o mesmo desenho.
     *
     * O ângulo é medido do topo, no sentido do relógio, igual ao
     * `conic-gradient`: as duas contas precisam concordar ou o número cai na
     * fatia vizinha.
     *
     * O número também **gira** com o ângulo, para deitar na tangente do arco em
     * vez de cruzar a faixa na diagonal. É curvatura do bloco, e não de cada
     * dígito: com três dígitos a flecha entre a corda e o arco dá 1,4px numa
     * faixa de 19px, e corrigir isso pediria `textPath` em SVG.
     *
     * Fatia na metade de baixo gira meia volta a menos. Girar o ângulo cheio ali
     * deixaria o número de cabeça para baixo — é o mesmo motivo pelo qual texto
     * circular em SVG precisa de um segundo path, com o sentido invertido.
     *
     * @param {String[]} conditions Condições dos Ativos escondidos.
     * @param {Array} shares O que `ringShares` devolveu para essas Condições.
     * @returns {Array} Condição, contagem, deslocamento em fração e giro em grau.
     */
    ringLabels(conditions, shares) {
      let start = 0;

      return shares.map(([condition, share]) => {
        const middle = start + (share / 2);
        const degrees = middle * 360;
        const angle = middle * 2 * Math.PI;
        const upsideDown = degrees > 90 && degrees < 270;

        start += share;

        return {
          condition,
          count: conditions.filter((item) => item === condition).length,
          rotation: upsideDown ? degrees - 180 : degrees,
          show: share >= MIN_LABEL_SHARE,
          x: RING_LABEL_RADIUS * Math.sin(angle),
          y: -RING_LABEL_RADIUS * Math.cos(angle),
        };
      }).filter((label) => label.show);
    },
    /**
     * O agrupamento mostra a composição, e não só o pior.
     *
     * O círculo cheio de vermelho dizia que tudo ali dentro era crítico, quando
     * podia ser um em duzentos. O anel diz a proporção, o número de cada fatia
     * diz a contagem, e o número do centro diz o total — em tinta neutra, porque
     * vestido da pior Condição ele repetia em letra garrafal o que o anel já
     * mostra, e fazia o agrupamento inteiro parecer crítico outra vez.
     */
    createClusterIcon(cluster) {
      const conditions = cluster
        .getAllChildMarkers()
        .map((marker) => marker.options.assetCondition);
      const worst = getWorstCondition(conditions);
      const count = cluster.getChildCount();
      const art = this.showIcons
        ? glyph('assets-map__cluster-glyph', INSTALLATION_GLYPH)
        : '';

      const size = Math.round(CLUSTER_BASE * this.markerScale());
      const shares = this.ringShares(conditions);
      // Uma Condição só não tem proporção para mostrar: o anel de uma cor só
      // seria um jeito mais fraco de dizer o que o círculo cheio diz na hora.
      // "Uma só" é exatamente uma, e não "quase tudo" — um crítico entre
      // duzentos normais continua com anel, que é o caso para o qual o anel
      // existe.
      const uniform = shares.length < 2;
      const modifier = uniform ? ' assets-map__cluster--uniform' : '';
      const ring = uniform ? '' : `; --cluster-ring: ${this.ringGradient(shares)}`;
      const slices = uniform || !this.showSliceCounts
        ? ''
        : this.ringLabels(conditions, shares).map((label) => `
          <span class="assets-map__cluster-slice assets-map__cluster-slice--${label.condition}"
            style="--slice-x: ${label.x.toFixed(4)}; --slice-y: ${label.y.toFixed(4)}; --slice-angle: ${label.rotation.toFixed(2)}deg"
          >${label.count}</span>`).join('');

      return L.divIcon({
        className: 'assets-map__cluster-wrapper',
        iconSize: [size, size],
        html: `<span class="assets-map__cluster assets-map__cluster--${worst}${modifier}"
          style="--cluster-size: ${size}px${ring}">
          ${art}
          <span class="assets-map__cluster-count">${count}</span>
          ${slices}
        </span>`,
      });
    },
    /** O desenho do tipo do Ativo, com o genérico para quem ainda não tem arte. */
    buildAssetGlyph(asset) {
      if (!this.showIcons) {
        return '';
      }

      const href = ASSET_GLYPHS[asset.type];

      return href
        ? glyph('assets-map__pin-glyph assets-map__pin-glyph--art', href)
        : `<i class="assets-map__pin-glyph ${GENERIC_ASSET_GLYPH}" aria-hidden="true"></i>`;
    },
    /**
     * Quanto o marcador cresce em relação ao tamanho base. Mapa mais largo,
     * marcador maior — até o teto. Sem largura medida (primeiro desenho, teste),
     * vale 1 e nada muda.
     */
    markerScale() {
      const width = this.$refs.map ? this.$refs.map.clientWidth : 0;

      if (!width) {
        return 1;
      }

      return Math.min(MAX_SCALE, Math.max(1, width / SCALE_REFERENCE));
    },
    /**
     * Escala do balão: acompanha o marcador, mas com teto próprio.
     */
    popupScale() {
      return Math.min(POPUP_MAX_SCALE, this.markerScale());
    },
    /**
     * Largura máxima do balão, entregue ao Leaflet em pixel.
     *
     * O teto é uma fração da largura do mapa, e não um valor fixo: num mapa
     * estreito — tela pequena, ou a lista de prioritários comendo espaço — um
     * balão de largura fixa transborda a vista e some com o mapa. Sem largura
     * medida ainda, vale o teto absoluto.
     */
    popupMaxWidth() {
      const mapWidth = this.$refs.map ? this.$refs.map.clientWidth : 0;
      const byScale = POPUP_MAX_WIDTH * this.popupScale();
      const byMap = mapWidth ? mapWidth * POPUP_MAX_WIDTH_RATIO : byScale;

      return Math.round(Math.max(POPUP_MIN_WIDTH, Math.min(byScale, byMap)));
    },
    createMarkerIcon(asset) {
      const size = Math.round(PIN_BASE * this.markerScale());
      const height = Math.round(size * PIN_HEIGHT_RATIO);

      return L.divIcon({
        className: 'assets-map__pin-wrapper',
        iconSize: [size, height],
        iconAnchor: [Math.round(size / 2), height],
        popupAnchor: [0, -Math.round(height * 0.9)],
        html: `<span class="assets-map__pin assets-map__pin--${asset.condition}"
          style="--pin-size: ${size}px">
          ${this.buildAssetGlyph(asset)}
        </span>`,
      });
    },
    /**
     * Os Alarmes Atuados do Ativo, em uma linha: `2 crítico(s) · 1 ordinário(s)`.
     *
     * Substitui o nome de um alarme só, que era o que o balão mostrava antes. Um
     * nome escondia quantos outros existiam — um Ativo com cinco atuados parecia
     * ter um. A contagem diz o tamanho do problema, que é o que se decide olhando
     * de longe; o nome de cada alarme está na lista ao lado.
     *
     * Autodiagnóstico conta separado e sem classificação, porque a classificação
     * dele não muda nada: ele lê sempre como Atenção. Misturá-lo às outras
     * contagens somaria coisas que a tela trata por regras diferentes.
     */
    describeAlarms(asset) {
      const chips = countedClassifications
        .map((classification) => ({
          classification,
          count: countActedAlarms(
            asset.actedAlarms,
            [alarmCategories.ALARM],
            [classification],
          ),
        }))
        .filter(({ count }) => count > 0)
        .map(({ classification, count }) => this.$t(
          `attention-map-panel.alarm-count.${classification}`,
          { count },
        ));

      const selfDiagnosis = countActedAlarms(
        asset.actedAlarms,
        [alarmCategories.SELF_DIAGNOSIS],
      );

      if (selfDiagnosis) {
        chips.push(this.$t(
          'attention-map-panel.alarm-count.self-diagnosis',
          { count: selfDiagnosis },
        ));
      }

      return escapeHtml(
        chips.length
          ? chips.join(' · ')
          : this.$t('attention-map-panel.no-alarm'),
      );
    },
    /**
     * Conteúdo do popup como HTML, e não como componente: o Leaflet gerencia o
     * ciclo de vida do balão por conta própria, e montar um componente Vue a cada
     * abertura só para exibir texto custaria mais do que entrega.
     */
    buildPopup(asset) {
      const conditionLabel = escapeHtml(
        this.$t(`attention-map-panel.condition.${asset.condition}`),
      );
      // O Pendente não pinta o marcador, então o popup é onde ele aparece.
      const pending = asset.pendingAlarm
        ? `<p class="assets-map__popup-pending">${escapeHtml(
          this.$t('attention-map-panel.alarm-pending'),
        )}</p>`
        : '';
      // O balão escala junto com o marcador: a conta é em pixel porque o Leaflet
      // ancora o balão por pixel, e porque containment num elemento que ele mede
      // por conta própria quebra o posicionamento.
      const scale = this.popupScale().toFixed(3);

      return `
        <div class="assets-map__popup" style="--popup-scale: ${scale}">
          <p class="assets-map__popup-title">
            ${escapeHtml(asset.name)} · ${escapeHtml(asset.installation)}
          </p>
          <p class="assets-map__popup-condition assets-map__popup-condition--${asset.condition}">
            <i class="${CONDITION_GLYPHS[asset.condition]}" aria-hidden="true"></i>
            <span>${conditionLabel}</span>
          </p>
          <p class="assets-map__popup-alarm">${this.describeAlarms(asset)}</p>
          ${pending}
          <button type="button" class="btn btn-outline-green btn-sm assets-map__popup-action">
            <i class="fas fa-external-link-alt" aria-hidden="true"></i>
            <span>${escapeHtml(this.$t('attention-map-panel.analyze-asset'))}</span>
          </button>
        </div>
      `;
    },
    drawMarkers() {
      if (!this.clusters) {
        return;
      }

      // Redesenha só a camada de marcadores: zoom, centro e a navegação de quem
      // está olhando sobrevivem ao ciclo de atualização.
      this.clusters.clearLayers();

      const markers = this.markableAssets.map((asset) => {
        const marker = L.marker([asset.latitude, asset.longitude], {
          icon: this.createMarkerIcon(asset),
          assetCondition: asset.condition,
          title: `${asset.name} · ${asset.installation}`,
        });

        marker.bindPopup(this.buildPopup(asset), {
          maxWidth: this.popupMaxWidth(),
          minWidth: POPUP_MIN_WIDTH,
        });
        marker.on('popupopen', (event) => this.bindPopupAction(event, asset));

        return marker;
      });

      this.clusters.addLayers(markers);
      this.fitOnce(markers);
      this.openStuckClusters();
      // O primeiro enquadramento também é um recorte, e a lista precisa dele:
      // sem isto ela ficaria sem conteúdo até o primeiro gesto do usuário.
      this.scheduleViewportChange();
    },

    /**
     * Abre sozinho o leque dos Ativos que dividem a mesma coordenada.
     *
     * Um agrupamento assim não se desfaz por zoom — todos os Ativos estão no
     * mesmo ponto, que é a Instalação. Antes era preciso clicar para ver o que
     * havia dentro; numa TV de parede não há quem clique.
     *
     * Abre **um** leque, e só quando há um só agrupamento desses na tela. O
     * Leaflet.markercluster mantém no máximo um leque aberto por vez — abrir o
     * segundo fecha o primeiro —, então com dois ou mais a escolha seria
     * arbitrária e o operador veria leques piscando sem saber por quê. Com
     * vários, quem escolhe continua sendo quem olha, pelo clique.
     */
    openStuckClusters() {
      if (!this.clusters || this.map.getZoom() < AUTO_SPIDERFY_ZOOM) {
        return;
      }

      // Já há um leque aberto: não encosta. Pode ter sido aberto pelo clique de
      // quem está olhando, e o Leaflet.markercluster mantém **um** leque por vez
      // — abrir outro fecharia este. Sem esta guarda, com duas Instalações
      // próximas os dois leques se fecham um ao outro indefinidamente.
      // eslint-disable-next-line no-underscore-dangle
      if (this.clusters._spiderfied) {
        return;
      }

      // `_featureGroup` é o que guarda os agrupamentos desenhados; o
      // Leaflet.markercluster não expõe essa lista de outro jeito.
      // eslint-disable-next-line no-underscore-dangle
      const drawn = this.clusters._featureGroup;

      if (!drawn) {
        return;
      }

      const stuck = [];

      drawn.eachLayer((layer) => {
        if (layer.spiderfy && this.isSingleSpot(layer)) {
          stuck.push(layer);
        }
      });

      if (stuck.length === 1) {
        stuck[0].spiderfy();
      }
    },

    /**
     * Agrupamento em que todos os marcadores caem no mesmo ponto.
     *
     * A coordenada é lida de `_preSpiderfyLatlng` quando existe: enquanto o leque
     * está aberto, o Leaflet.markercluster **move** os marcadores para as pernas
     * e guarda o ponto original ali. Ler a posição corrente faria um agrupamento
     * aberto deixar de parecer preso, que foi a origem do laço de abre-e-fecha.
     */
    isSingleSpot(cluster) {
      // eslint-disable-next-line no-underscore-dangle
      const spotOf = (marker) => marker._preSpiderfyLatlng || marker.getLatLng();
      const markers = cluster.getAllChildMarkers();
      const first = markers.length ? spotOf(markers[0]) : null;

      return !!first && markers.every((marker) => spotOf(marker).equals(first));
    },
    /**
     * Enquadra os marcadores no primeiro desenho com dado, e só nele: um escopo
     * de uma instalação abriria no Brasil inteiro. Dos ciclos seguintes em
     * diante o enquadramento é de quem está olhando, e o mapa não se mexe sob o
     * dedo de ninguém.
     */
    fitOnce(markers) {
      if (this.hasFitted || !markers.length) {
        return;
      }

      this.hasFitted = true;
      this.map.fitBounds(L.latLngBounds(markers.map((marker) => marker.getLatLng())), {
        maxZoom: FIT_MAX_ZOOM,
        padding: FIT_PADDING,
      });
    },
    /**
     * Liga o botão do balão à saída da tela.
     *
     * O clique do meio também vale, como no "Acessar" da lista de ativos: quem
     * vigia costuma abrir vários ativos em abas sem largar o painel, e o botão do
     * meio é o gesto de sempre para isso.
     */
    bindPopupAction(event, asset) {
      const element = event.popup.getElement();
      const button = element && element.querySelector('.assets-map__popup-action');

      if (button) {
        const select = () => this.$emit('select', asset);

        button.addEventListener('click', select);
        button.addEventListener('mousedown', (mouse) => {
          if (mouse.button === 1) {
            mouse.preventDefault();
            select();
          }
        });
      }
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/components/communication-status-panel-fluid"

.assets-map
  // Container das medidas da Legenda, que fica por cima do mapa: ela tem
  // largura de conteúdo e não pode medir a si mesma.
  container-name: assets-map
  container-type: inline-size
  height: 100%
  position: relative
  width: 100%

  &__canvas
    height: 100%
    width: 100%

  &__empty
    background: var(--panel-surface)
    border-radius: 0.5rem
    color: var(--panel-text-muted)
    +panel-fluid(font-size, 1rem, 1.6cqw, 2.4rem)
    left: 50%
    margin: 0
    padding: 0.75rem 1.25rem
    position: absolute
    top: 50%
    transform: translate(-50%, -50%)
    // Acima dos tiles do Leaflet, que já usam z-index proprio.
    z-index: 500

  &__legend
    bottom: 1rem
    left: 1rem
    position: absolute
    z-index: 500
</style>
